/**
 * sd-brand-values.js
 *
 * Brand Values section — two JS-first animations:
 *   1. Text-roll headline (recipe #6): the .roll wrapper gets .is-visible when
 *      it enters the viewport; CSS keyframes then stagger each .roll__w word up.
 *   2. Staggered reveal (recipe #1): every .reveal element fades + slides in.
 *
 * One IntersectionObserver handles both — anything with .reveal or .roll is observed.
 * Pure IO + CSS → runs in Chrome, Safari, Firefox, Edge. No animation-timeline.
 * Reduced motion: everything visible immediately via CSS (no JS special-casing needed
 * beyond the immediate classList add in prefersReducedMotion branch).
 */

import { Component } from '@theme/component';
import { prefersReducedMotion } from '@theme/utilities';

class SdBrandValues extends Component {
  #io = null;

  connectedCallback() {
    super.connectedCallback();

    if (prefersReducedMotion()) {
      /* CSS already makes .reveal and .roll__w visible under reduce; add the
         class so any JS-conditional logic also sees the section as done. */
      this.querySelectorAll('.reveal, .roll').forEach((el) =>
        el.classList.add('is-visible')
      );
      return;
    }

    this.#io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            this.#io?.unobserve(entry.target);
          }
        }
      },
      { rootMargin: '0px 0px -8% 0px' }
    );

    /* Observe both plain reveals AND the roll wrapper (heading). */
    this.querySelectorAll('.reveal, .roll').forEach((el) => this.#io.observe(el));
  }

  updatedCallback() {
    super.updatedCallback?.();
    if (prefersReducedMotion()) return;
    /* Re-observe anything that appeared after a theme-editor block add/remove. */
    this.querySelectorAll('.reveal:not(.is-visible), .roll:not(.is-visible)').forEach(
      (el) => this.#io?.observe(el)
    );
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#io?.disconnect();
  }
}

if (!customElements.get('sd-brand-values')) {
  customElements.define('sd-brand-values', SdBrandValues);
}
